/**
 * Bumper — the radial knockback orb (Brief 014). SHARED across Sonic (bumper) and
 * Celeste (bumper): touch it and it flings the player straight AWAY from its center.
 *
 * A non-solid OVERLAP entity. On contact it sets the player's velocity to a vector
 * pointing from the bumper's center to the player's center, scaled to `props.power`
 * (px/s). A short integer cooldown stops it from machine-gunning while you sit in it.
 *
 * Determinism: Math.sqrt is lint-allowed (the existing 8-way dash normalizes the same
 * way); integer cooldown + sign/abs only → ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_POWER = 900;
const HIT_COOLDOWN_STEPS = 14;

export class Bumper implements Entity {
  readonly type = EntityType.BUMPER;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private power: number;
  private cooldown = 0;
  private flash = 0;

  /** @param props `power` (px/s radial launch; default 900). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const p = props?.power;
    this.power = typeof p === 'number' && p > 0 ? p : DEFAULT_POWER;
  }

  update(_dt: number, _ctx: EntityContext): void {
    if (this.cooldown > 0) this.cooldown--;
    if (this.flash > 0) this.flash--;
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.cooldown > 0) return;
    const pb = ctx.player.body;
    const cx = this.body.x + this.body.w / 2;
    const cy = this.body.y + this.body.h / 2;
    let dx = (pb.x + pb.w / 2) - cx;
    let dy = (pb.y + pb.h / 2) - cy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) { dx = 0; dy = -1; } // dead-center → fling straight up
    else { dx /= len; dy /= len; }
    ctx.player.vx = dx * this.power;
    ctx.player.vy = dy * this.power;
    ctx.player.grounded = false;
    this.cooldown = HIT_COOLDOWN_STEPS;
    this.flash = 8;
    ctx.playSound('spring', 0.5, 1.1); // POLISH_01 #1 — boing on the radial fling
    // POLISH_01 #10 — radial pop at the orb + a body squash + trauma along the fling axis.
    ctx.emitFx?.('burst', cx, cy, { count: 6, color: 0x8be9fd });
    ctx.launchFx?.(dx, dy);
  }

  spriteKey(): string { return this.flash > 0 ? 'bumper_hit' : 'bumper'; }

  visible(): boolean { return true; }
}
